import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { ISiteRoleFilter } from '../../ts/filters/SiteRoleFilter';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { ServerUrls } from '../ServerUrls';

export module ServerUsersGrid {

  function isUserReadonlyOrGuest(user: IServerUserInfo) {
    return user.readOnly || user.maxSiteRole === SiteRoles.Guest;
  }

  export function licenseLevelTransform(translate: IReactTranslateService): (licenseRole: string) => string {
    return (licenseRole: string) => licenseRole ? translate('license_' + licenseRole) : '';
  }

  export function columns(translate: IReactTranslateService,
                          scope: DataGridCells.ISelectedItemsScope<IServerUserInfo>,
                          serverUrls: ServerUrls,
                          siteRoleFilter: ISiteRoleFilter,
                          actions: IActionButton<IServerUserInfo, {}>[],
                          getActionTarget: () => IActionTarget<IServerUserInfo, {}>,
                          showDomainNameColumn: boolean,
                          showLicenseLevelColumn: boolean): DataGrid.Column[] {

    const userDisplayNameColumnWithActions = CommonDataGridColumns.userDisplayNameColumn({
      actions: actions,
      getActionTarget: getActionTarget,
      linkDisabled: isUserReadonlyOrGuest,
      menuDisabled: (row: DataGrid.Row) => isUserReadonlyOrGuest(row.data as IServerUserInfo),
      scope: scope,
      translate: translate,
      url: (user: IServerUserInfo) => serverUrls.userSettingsUrl(user.domainName, user.username)
    });

    const userNameColumn = {
      name: 'username-col',
      dataKey: 'username',
      headerText: translate('column_username'),
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc'
    };

    const maxSiteRoleColumn = {
      name: 'maxSiteRole-col',
      dataKey: 'maxSiteRole',
      headerText: translate('column_max_site_role'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.PlainTextCellWithTransform('maxSiteRole',
        (maxSiteRole: string) => siteRoleFilter(maxSiteRole))
    };

    const licenseLevelColumn = {
      name: 'licenseLevel-col',
      dataKey: 'subscriptionLicensingRole',
      headerText: translate('column_license_level'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.PlainTextCellWithTransform('subscriptionLicensingRole', licenseLevelTransform(translate)),
      direction: 'asc'
    };

    const siteCountColumn = {
      name: 'siteCount-col',
      dataKey: 'siteCount',
      headerText: translate('column_site_count'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('siteCount')),
      direction: 'asc',
    };

    const domainNameColumn = {
      name: 'domainName-col',
      dataKey: 'domainName',
      headerText: translate('column_domainName'),
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc'
    };

    const lastSignInColumn = {
      name: 'lastSignIn-col',
      dataKey: 'lastSignIn',
      headerText: translate('column_last_sign_in'),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('lastSignIn')
    };

    return _.compact([
      CommonDataGridColumns.selected(scope),
      userDisplayNameColumnWithActions,
      userNameColumn,
      maxSiteRoleColumn,
      showLicenseLevelColumn && licenseLevelColumn,
      siteCountColumn,
      showDomainNameColumn && domainNameColumn,
      lastSignInColumn
    ]);
  }

}
